/** @format */

import { useEffect, useState, useEffectEvent, useRef } from 'react'
import { useMutation } from '@tanstack/react-query'
import { FRESHNESS, useRequest, useQuery, useEntityInvalidate } from '@/shared/api'
import { getCookie, setCookie } from '@/shared/lib/formatter'
import { Toast } from '@/shared/ui/organisms/toast'
import {
  getMessagingToken,
  setupOnMessage,
  initializeMessaging,
  requestNotificationPermission,
  deleteMessagingToken,
  logAnalyticsEvent,
} from '../../firebase-config'
import { useNotificationsStore } from '@/entities/hr/notifications/model'
import { useSafeNavigate } from '@/shared/lib/safe-navigate'

const NotificationHandler = () => {
  const { request } = useRequest()
  const navigate = useSafeNavigate()
  const [isTokenRegistered, setIsTokenRegistered] = useState(false)
  const firebaseSetupDone = useRef(false)
  const rawUserId = getCookie('userId')
  const userId = rawUserId && rawUserId !== 'null' && rawUserId !== 'undefined' ? rawUserId : null
  const isValidUserId = !!userId
  const isLoggedIn = !!getCookie('token')
  const setUserNotifications = useNotificationsStore((s) => s.setNotifications)
  const setTotalUnreadMessages = useNotificationsStore((s) => s.setTotalUnreadMessages)
  const setTotalNotifications = useNotificationsStore((s) => s.setTotalNotifications)
  const invalidateNotifications = useEntityInvalidate('/v1/notifications')

  // Mutation to save FCM token to backend
  const saveTokenMutation = useMutation<any, any, any>({
    mutationFn: (values) => {
      return request({
        url: '/v1/fcm/me/firebase-details',
        method: 'POST',
        body: values,
      })
    },
    onSuccess: () => {
      setIsTokenRegistered(true)
      logAnalyticsEvent('fcm_token_registered')
    },
    onError: (error) => {
      console.error('Failed to register notification token with server', error)
    },
  })

  // Fetch notifications — TQ v5 native pattern: data + useEffect for store sync.
  // `select` must be a pure function; side effects there can trigger render loops.
  const { data: notificationsData } = useQuery<any>(
    ['/v1/notifications', userId],
    () =>
      request({
        url: `/v1/notifications?user_id=${userId}&read=ALL&page=0&size=10&sort=id,desc`,
      }),
    { enabled: isValidUserId, refetchOnWindowFocus: false, staleTime: FRESHNESS.REFERENCE },
  )

  useEffect(() => {
    if (!notificationsData?.success) return
    setUserNotifications(notificationsData.data)
    setTotalUnreadMessages(notificationsData?.meta?.unreadCount)
    setTotalNotifications(notificationsData?.meta?.total)
  }, [notificationsData, setUserNotifications, setTotalUnreadMessages, setTotalNotifications])

  // Firebase setup — runs once per login
  const onSetupFirebase = useEffectEvent(async () => {
    if (firebaseSetupDone.current) return undefined
    firebaseSetupDone.current = true

    try {
      await initializeMessaging()

      const permission = await requestNotificationPermission()
      if (permission !== 'granted') return undefined

      const currentToken = await getMessagingToken(undefined)
      if (!currentToken) return undefined

      setCookie('firebaseToken', currentToken)
      setCookie('firebaseType', 'WEB')

      const existingToken = getCookie('registeredFirebaseToken')
      if (existingToken !== currentToken) {
        await saveTokenMutation.mutateAsync({
          token: currentToken,
          type: 'WEB',
        })
        setCookie('registeredFirebaseToken', currentToken)
      } else {
        setIsTokenRegistered(true)
      }

      // Fetch firebase settings (one-time, fire-and-forget)
      if (userId) {
        request({ url: `/v1/fcm/${userId}/firebase-details` }).catch(() => {})
      }

      // Setup foreground message handler
      const unsub = await setupOnMessage((payload: any) => {
        invalidateNotifications()
        ;(Toast as any)({
          type: 'success',
          title: payload?.notification?.title || 'New notification',
          message: payload?.notification?.body || '',
          duration: 5000,
          onClick: () => {
            const link = payload?.data?.url || payload?.data?.['event-link']
            if (link) {
              try {
                const parsed = new URL(link)
                navigate(parsed.pathname + parsed.search)
              } catch {
                if (link.startsWith('/') && !link.startsWith('//')) {
                  navigate(link)
                }
              }
            }
          },
        })
        logAnalyticsEvent('notification_received', {
          title: payload?.notification?.title,
          inForeground: true,
        })
      })
      return unsub
    } catch (error) {
      console.error('Failed to setup notifications', error)
      firebaseSetupDone.current = false
      return undefined
    }
  })

  // Setup Firebase notifications — once per login
  useEffect(() => {
    if (!isLoggedIn) {
      firebaseSetupDone.current = false
      return
    }

    let unsubscribe: any

    onSetupFirebase().then((unsub) => {
      unsubscribe = unsub
    })

    return () => {
      if (unsubscribe) unsubscribe()
    }
  }, [isLoggedIn, userId, navigate, onSetupFirebase])

  // Handle logout - cleanup token
  useEffect(() => {
    const handleLogout = async () => {
      if (!isLoggedIn && isTokenRegistered) {
        try {
          await deleteMessagingToken()
          setCookie('firebaseToken', '', -1)
          setCookie('registeredFirebaseToken', '', -1)
          setCookie('firebaseType', '', -1)
          setIsTokenRegistered(false)
        } catch (error) {
          console.error('Failed to clean up Firebase token', error)
        }
      }
    }

    handleLogout()
  }, [isLoggedIn, isTokenRegistered])

  return null
}

export default NotificationHandler
